import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Download, ChevronRight, Home } from "lucide-react";
import Seo from "@/components/Seo";
import PageShell from "@/components/PageShell";
import { MACHINES } from "@/lib/pages-data";
import { BROCHURES, BRAND } from "@/lib/brand";

const SITE = "https://thecoventures.com";

export default function ProductPage() {
  const { slug } = useParams();
  const m = MACHINES[slug];
  if (!m) return <Navigate to="/products" replace />;

  const canonical = `${SITE}/products/${m.slug}`;
  const brochure = BROCHURES[m.brochureKey];

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: m.name,
    description: m.metaDescription,
    image: m.heroImg,
    brand: { "@type": "Brand", name: "Refuel by Thecoventures" },
    category: m.tag,
    manufacturer: { "@type": "Organization", name: BRAND.name, url: SITE + "/" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: "150000",
      offerCount: "1",
      availability: "https://schema.org/InStock",
      url: canonical,
      seller: { "@type": "Organization", name: BRAND.name },
    },
    additionalProperty: m.specs.map(([k, v]) => ({
      "@type": "PropertyValue",
      name: k,
      value: v,
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Vending Machines", item: SITE + "/products" },
      { "@type": "ListItem", position: 3, name: m.name, item: canonical },
    ],
  };

  return (
    <PageShell>
      <Seo
        title={m.metaTitle}
        description={m.metaDescription}
        canonical={canonical}
        keywords={m.keyword}
        image={m.heroImg}
        jsonLd={[productSchema, breadcrumbSchema]}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6"
          data-testid="product-breadcrumbs"
        >
          <Link to="/" className="hover:text-zinc-900 inline-flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-zinc-900">Vending Machines</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-900">{m.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5 mb-5">
              {m.tag}
            </div>
            <h1
              data-testid="product-h1"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950 leading-[1.02]"
            >
              {m.h1}
            </h1>
            <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-xl">{m.subh}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/#contact"
                data-testid="product-cta-quote"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-6 py-3 shadow-[0_10px_24px_rgba(16,185,129,0.3)] transition"
              >
                Get a Free Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              {brochure && (
                <a
                  href={brochure.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-brochure"
                  className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 text-zinc-900 font-semibold rounded-full px-5 py-3 border border-zinc-200 transition"
                >
                  <Download className="w-4 h-4" /> Download brochure
                </a>
              )}
            </div>
          </div>

          <div className="relative rounded-3xl overflow-hidden border border-zinc-200 bg-zinc-50 aspect-[4/3] shadow-lg shadow-zinc-900/5">
            <img
              src={m.heroImg}
              alt={`${m.name} — ${m.tag} vending machine India`}
              loading="eager"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
              Highlights
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-zinc-950">
              Why teams choose {m.name}
            </h2>
            <ul className="mt-6 space-y-3">
              {m.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-3 text-zinc-700"
                  data-testid="product-highlight"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-600 mb-3">
              Specifications
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-zinc-950">
              Spec sheet
            </h2>
            <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
              {m.specs.map(([k, v], i) => (
                <div
                  key={k}
                  className={`flex items-start justify-between gap-6 px-5 py-3.5 text-sm ${
                    i !== m.specs.length - 1 ? "border-b border-zinc-100" : ""
                  }`}
                >
                  <div className="text-zinc-500 uppercase tracking-wider text-[11px]">{k}</div>
                  <div className="text-zinc-900 text-right">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          Compare the range
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.values(MACHINES).map((x) => (
            <Link
              key={x.slug}
              to={`/products/${x.slug}`}
              data-testid={`related-machine-${x.slug}`}
              className={`rounded-2xl border p-5 transition ${
                x.slug === m.slug
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-zinc-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{x.tag}</div>
              <div className="mt-1.5 font-display text-lg font-semibold text-zinc-950">
                {x.name}
              </div>
              <div className="mt-2 text-xs text-zinc-600 line-clamp-2">{x.subh}</div>
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
